import {ChatApiError, ChatClient} from "@emb-chat/react-native-sdk";
import {appConfig} from "@/app-config";
import {getChatToken} from "@/data/chat-api";

// One shared, lazily-connected ChatClient — plus the machinery to REPLACE it.
//
// The SDK takes its connect token as a fixed string: RestClient sends it on every request and
// ChatSocket hands it to socket.io's handshake, and neither can be re-pointed at a new token. So
// "refreshing the token" here means minting a second one, building a second client, and retiring
// the first. Everything below exists to make that swap safe: one mint however many callers race
// it, the old client always disconnected, and mounted consumers told to rebuild — because a
// Channel handle belongs to the client that made it and dies with it.

interface ChatSession {
    client: ChatClient;
    /**
     * Device-clock deadline, derived from a DURATION the backend reports rather than an absolute
     * timestamp, so only elapsed time matters and a skewed device clock cannot mistime the swap.
     */
    expiresAt: number;
}

// Rebuild well before the token actually dies. The socket re-presents it on every reconnect and a
// handshake the gateway refuses is terminal — socket.io does not retry a rejected one — so reaching
// expiry with a live socket costs the user live delivery, not merely a failed request.
const EXPIRY_MARGIN_MS = 5 * 60_000;
// Backend too old to report a lifetime: assume something short rather than something wrong.
const FALLBACK_TTL_SECONDS = 15 * 60;

let sessionPromise: Promise<ChatSession> | null = null;
let liveSession: ChatSession | null = null;
let remintTimer: ReturnType<typeof setTimeout> | null = null;
// Clients we have already replaced. Lets every caller holding the same dead client join one
// refresh instead of each minting a token of its own.
const retiredClients = new WeakSet<ChatClient>();

let generation = 0;
const listeners = new Set<() => void>();

function clearRemintTimer(): void {
    if (!remintTimer) return;
    clearTimeout(remintTimer);
    remintTimer = null;
}

/**
 * Swap the client shortly before its token dies, so a conversation left open past the hour keeps
 * receiving messages. Only dependable while the app is foregrounded — the OS throttles timers in
 * the background, which is what the resume hook in the chat hooks covers.
 */
function armRemintTimer(session: ChatSession): void {
    clearRemintTimer();
    const delay = session.expiresAt - EXPIRY_MARGIN_MS - Date.now();
    if (delay <= 0) return;
    remintTimer = setTimeout(() => {
        remintTimer = null;
        void refreshChatClient(session.client).catch(() => undefined);
    }, delay);
}

async function createSession(): Promise<ChatSession> {
    if (!appConfig.chatUrl) {
        throw new Error("EXPO_PUBLIC_CHAT_URL is not configured");
    }
    const {token, expiresInSeconds} = await getChatToken();
    const client = new ChatClient({url: appConfig.chatUrl, token});
    try {
        await client.connect();
    } catch (err) {
        client.disconnect();
        throw err;
    }
    const session: ChatSession = {
        client,
        expiresAt: Date.now() + (expiresInSeconds ?? FALLBACK_TTL_SECONDS) * 1000
    };
    liveSession = session;
    armRemintTimer(session);
    return session;
}

function startSession(): Promise<ChatSession> {
    sessionPromise = createSession().catch((err) => {
        sessionPromise = null; // allow retry on next access
        throw err;
    });
    return sessionPromise;
}

function isExpiring(session: ChatSession): boolean {
    return Date.now() >= session.expiresAt - EXPIRY_MARGIN_MS;
}

function retire(client: ChatClient): void {
    retiredClients.add(client);
    try {
        client.disconnect();
    } catch {
        // A client that never finished connecting has nothing to release, and a failed teardown
        // must never block building its replacement.
    }
}

function bumpGeneration(): void {
    generation += 1;
    for (const listener of listeners) listener();
}

/** A 401 from the chat REST API: the connect token is expired, revoked, or was never valid. */
export function isChatAuthError(err: unknown): boolean {
    return err instanceof ChatApiError && err.status === 401;
}

export async function getChatClient(): Promise<ChatClient> {
    if (!sessionPromise) return (await startSession()).client;
    const session = await sessionPromise;
    if (isExpiring(session)) return refreshChatClient(session.client);
    return session.client;
}

/**
 * Replace the shared client with one built from a freshly minted token.
 *
 * Pass the client you found broken: if it has already been replaced — by a caller that hit the same
 * expiry a moment earlier — this joins that refresh rather than starting another, so N simultaneous
 * failures cost one token, not N.
 */
export async function refreshChatClient(stale?: ChatClient): Promise<ChatClient> {
    if (stale && retiredClients.has(stale)) {
        return (await (sessionPromise ?? startSession())).client;
    }
    clearRemintTimer();
    const previous = liveSession;
    liveSession = null;
    const pending = startSession();
    if (previous) retire(previous.client);
    else if (stale) retiredClients.add(stale);
    // Consumers hold Channel handles bound to the client we just retired; tell them to rebuild.
    bumpGeneration();
    return (await pending).client;
}

/**
 * Run one piece of chat work, once more against a new client if the token died underneath it.
 *
 * `run` receives the client rather than closing over one, so the retry cannot reuse the dead one.
 * The second attempt is final: a 401 that survives a fresh token is not about the token.
 */
export async function withChatClient<T>(run: (client: ChatClient) => Promise<T>): Promise<T> {
    const client = await getChatClient();
    try {
        return await run(client);
    } catch (err) {
        // REST says 401 outright. The socket never does — a send over a socket the gateway refused
        // surfaces as an ack timeout or "socket not connected" — so for anything else we ask the
        // token's own deadline, which also stops a plain offline blip from minting.
        const expired = !!liveSession && liveSession.client === client && isExpiring(liveSession);
        if (!isChatAuthError(err) && !expired) throw err;
        return run(await refreshChatClient(client));
    }
}

/** Drop the shared client entirely (logout / user switch). The next access builds a new one. */
export function resetChatClient(): void {
    clearRemintTimer();
    const pending = sessionPromise;
    const live = liveSession;
    sessionPromise = null;
    liveSession = null;
    if (live) retire(live.client);
    // A connect still in flight has a client nobody has seen yet; retire it when it lands.
    else if (pending) void pending.then((session) => retire(session.client)).catch(() => undefined);
    if (pending) bumpGeneration();
}

// --- subscription, for consumers holding client-bound handles (see useChatClientGeneration) ---

export function subscribeToChatClient(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getChatClientGeneration(): number {
    return generation;
}
